import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import DateRangeFilter from '@/components/DateRangeFilter';
import { fetchLeads, updateLead, fetchParameters } from '@/lib/api';
import { formatINR } from '@/lib/format';
import { toast } from 'sonner';

const STAGES = ["Lead Identified", "Pitching Services", "Qualifying Deal", "Sending Proposal", "Negotiation", "Closure - Won", "Closure - Lost"];
const STAGE_COLOR = {
    'Lead Identified': '#3d3d3d',
    'Pitching Services': '#3b5470',
    'Qualifying Deal': '#4a5b8c',
    'Sending Proposal': '#8a6d2d',
    'Negotiation': '#a35a2a',
    'Closure - Won': '#2d6b3d',
    'Closure - Lost': '#8a2d2d',
};

export default function Pipeline() {
    const [leads, setLeads] = useState([]);
    const [params, setParams] = useState({});
    const [ownerFilter, setOwnerFilter] = useState('');
    const [winLossFilter, setWinLossFilter] = useState('');
    const [range, setRange] = useState({ from: null, to: null });
    const [dragId, setDragId] = useState(null);
    const nav = useNavigate();

    const load = async () => {
        const p = await fetchParameters();
        setParams(p);
        const query = {};
        if (ownerFilter) query.sales_owner = ownerFilter;
        if (winLossFilter) query.win_loss = winLossFilter;
        if (range.from) query.date_from = range.from;
        if (range.to) query.date_to = range.to;
        const l = await fetchLeads(query);
        setLeads(l);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [ownerFilter, winLossFilter, range]);

    const onDrop = async (stage) => {
        if (!dragId) return;
        const lead = leads.find(l => l.id === dragId);
        if (!lead || lead.stage === stage) { setDragId(null); return; }
        try {
            const updated = await updateLead(dragId, { stage });
            setLeads(leads.map(l => l.id === dragId ? updated : l));
            toast.success(`Moved ${lead.customer_name} → ${stage}`);
        } catch { toast.error('Failed to move lead'); }
        setDragId(null);
    };

    return (
        <div>
            <PageHeader
                title="Pipeline Board"
                subtitle="Drag & drop to move stages"
                actions={
                    <div className="flex items-center gap-2 flex-wrap">
                        <DateRangeFilter value={range} onChange={setRange} />
                        <select
                            value={winLossFilter}
                            onChange={e => setWinLossFilter(e.target.value)}
                            data-testid="pipeline-winloss-filter"
                            className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-md px-3 py-2 text-sm text-slate-900 h-9"
                        >
                            <option value="">Win/Loss: All</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Won">Win</option>
                            <option value="Lost">Lost</option>
                            <option value="Upcoming Months">Upcoming Month</option>
                        </select>
                        <select
                            value={ownerFilter}
                            onChange={e => setOwnerFilter(e.target.value)}
                            data-testid="pipeline-owner-filter"
                            className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-md px-3 py-2 text-sm text-slate-900 h-9"
                        >
                            <option value="">All Owners</option>
                            {(params.sales_owners || []).map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                }
            />
            <div className="p-6 overflow-x-auto">
                <div className="flex gap-4 min-w-max" data-testid="kanban-board">
                    {STAGES.map(stage => {
                        const items = leads.filter(l => l.stage === stage);
                        const total = items.reduce((s, l) => s + (l.proposal_value || 0), 0);
                        return (
                            <div
                                key={stage}
                                className="w-72 shrink-0"
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => onDrop(stage)}
                                data-testid={`kanban-col-${stage.replace(/\s+/g, '-')}`}
                            >
                                <div className="surface-1 rounded-t-md p-3 border-b-2" style={{ borderBottomColor: STAGE_COLOR[stage] }}>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-slate-900">{stage}</span>
                                        <span className="text-xs text-slate-500 tabular-nums">{items.length}</span>
                                    </div>
                                    <div className="text-xs text-slate-500 tabular-nums mt-0.5">{formatINR(total)}</div>
                                </div>
                                <div className="bg-[#F1F5F9] border border-t-0 border-[#E2E8F0] rounded-b-md p-2 min-h-[500px] space-y-2">
                                    {items.map(l => (
                                        <div
                                            key={l.id}
                                            draggable
                                            onDragStart={() => setDragId(l.id)}
                                            onClick={() => nav(`/leads/${l.id}`)}
                                            data-testid={`kanban-card-${l.lead_id}`}
                                            className="surface-1 rounded-md p-3 cursor-grab active:cursor-grabbing hover-lift"
                                        >
                                            <div className="text-xs text-slate-500 tabular-nums">{l.lead_id}</div>
                                            <div className="text-sm text-slate-900 font-medium mt-1 line-clamp-2">{l.customer_name}</div>
                                            <div className="text-xs text-slate-500 mt-1 truncate">{l.service_offered || l.service_line}</div>
                                            <div className="flex items-center justify-between mt-2">
                                                <span className="text-xs tabular-nums text-[#016a6d] font-medium">{formatINR(l.proposal_value)}</span>
                                                <span className="text-[10px] uppercase tracking-wider text-slate-500">{l.sales_owner}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
